import { computed } from 'vue'
import { theme } from 'ant-design-vue'
import type { ThemeConfig } from 'ant-design-vue/es/config-provider/context'
import { useTheme } from './useTheme'

export type ThemeMode = 'light' | 'dark' | 'auto'

// AntD 的调色算法需要实际色值；这里与 theme.css 的公共语义令牌保持一致。
const sharedTokens: ThemeConfig['token'] = {
  fontSize: 14,
  controlHeight: 36,
  borderRadius: 6,
  borderRadiusLG: 8,
}

// Ant Design Vue 暗色主题配置
const darkTheme: ThemeConfig = {
  algorithm: theme.darkAlgorithm,
  token: {
    ...sharedTokens,
    colorPrimary: '#60a5fa',
    colorPrimaryHover: '#93c5fd',
    colorPrimaryActive: '#3b82f6',
    colorPrimaryBg: '#172c4c',
    colorInfo: '#60a5fa',
    colorLink: '#60a5fa',
    colorLinkHover: '#93c5fd',
    colorLinkActive: '#3b82f6',
    colorBgBase: '#10151f',
    colorBgLayout: '#10151f',
    colorBgContainer: '#171e2b',
    colorBgElevated: '#202a3a',
    colorBgContainerDisabled: '#263247',
    colorFillTertiary: '#263247',
    colorText: '#f8fafc',
    colorTextSecondary: '#cbd5e1',
    colorTextTertiary: '#94a3b8',
    colorTextQuaternary: '#7c8ba1',
    colorTextDisabled: '#7c8ba1',
    colorTextPlaceholder: '#94a3b8',
    colorTextLightSolid: '#ffffff',
    colorBorder: '#334155',
    colorBorderSecondary: '#273449',
    controlItemBgHover: '#263247',
    controlItemBgActive: '#172c4c',
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.24)',
    boxShadowSecondary: '0 8px 24px rgba(0, 0, 0, 0.32)',
  },
  // 深色前景仅作用于亮蓝实心控件，提示层、预览遮罩继续使用白字。
  components: {
    Button: {
      colorTextLightSolid: '#10151f',
      colorError: '#f87171',
      colorErrorHover: '#fca5a5',
      colorErrorActive: '#ef4444',
    },
    Checkbox: { colorWhite: '#10151f' },
    Radio: { colorTextLightSolid: '#10151f', colorWhite: '#10151f' },
    Switch: { colorTextLightSolid: '#10151f', colorWhite: '#10151f' },
    Steps: { colorTextLightSolid: '#10151f' },
    DatePicker: { colorTextLightSolid: '#10151f' },
  },
}

// Ant Design Vue 亮色主题配置
const lightTheme: ThemeConfig = {
  algorithm: theme.defaultAlgorithm,
  token: {
    ...sharedTokens,
    colorPrimary: '#2563eb',
    colorPrimaryHover: '#1d4ed8',
    colorPrimaryActive: '#1e40af',
    colorPrimaryBg: '#eff6ff',
    colorInfo: '#2563eb',
    colorLink: '#2563eb',
    colorLinkHover: '#1d4ed8',
    colorLinkActive: '#1e40af',
    colorBgBase: '#ffffff',
    colorBgLayout: '#f5f7fa',
    colorBgContainer: '#ffffff',
    colorBgElevated: '#ffffff',
    colorBgContainerDisabled: '#f1f5f9',
    colorFillTertiary: '#f1f5f9',
    colorText: '#172033',
    colorTextSecondary: '#526078',
    colorTextTertiary: '#94a3b8',
    colorTextQuaternary: '#94a3b8',
    colorTextDisabled: '#94a3b8',
    colorTextPlaceholder: '#94a3b8',
    colorTextLightSolid: '#ffffff',
    colorBorder: '#e2e8f0',
    colorBorderSecondary: '#eef2f7',
    controlItemBgHover: '#f1f5f9',
    controlItemBgActive: '#eff6ff',
    boxShadow: '0 4px 16px rgba(15, 23, 42, 0.08)',
    boxShadowSecondary: '0 8px 24px rgba(15, 23, 42, 0.12)',
  },
}

export function useAntdTheme() {
  const { themeMode, actualTheme, toggleTheme, setTheme } = useTheme()

  // 获取 Ant Design Vue 的主题配置
  const antdTheme = computed<ThemeConfig>(() => {
    return actualTheme.value === 'dark' ? darkTheme : lightTheme
  })

  // 获取主题图标
  const themeIcon = computed(() => {
    switch (themeMode.value) {
      case 'light':
        return '☀️'
      case 'dark':
        return '🌙'
      case 'auto':
        return '🔄'
      default:
        return '☀️'
    }
  })

  // 获取主题显示名称
  const themeName = computed(() => {
    switch (themeMode.value) {
      case 'light':
        return '亮色模式'
      case 'dark':
        return '暗色模式'
      case 'auto':
        return '跟随系统'
      default:
        return '亮色模式'
    }
  })

  return {
    themeMode,
    actualTheme,
    antdTheme,
    toggleTheme,
    setTheme,
    themeIcon,
    themeName,
  }
}

// 使用示例：
// const { themeMode, actualTheme, antdTheme, toggleTheme } = useAntdTheme()
//
// <a-config-provider :theme="antdTheme">
//   <button @click="toggleTheme">
//     {{ themeIcon }} {{ themeName }}
//   </button>
// </a-config-provider>
